import { Injectable } from '@angular/core';

export enum TOKEN_FEATURE {
    CONTRACT_CHAT = 'CONTRACT_CHAT',
    COMPARE_CHAT = 'COMPARE_CHAT',
    COMPARE = 'COMPARE',
    GENERATE_WITH_AI = 'GENERATE_WITH_AI',
    TAG_EXTRACTION = 'TAG_EXTRACTION',
    CHECKLIST = 'CHECKLIST',
    AI_TOOLING = 'AI_TOOLING',
    SUMMARY = 'SUMMARY',
    GEOTECHNICAL_SUMMARY = 'GEOTECHNICAL_SUMMARY',
    GEOTECHNICAL_ANALYSIS_SUMMARY = 'GEOTECHNICAL_ANALYSIS_SUMMARY',
    GEOTECHNICAL_CHAT = 'GEOTECHNICAL_CHAT',
    MISCELLANEOUS = 'MISCELLANEOUS',
    DUPLICATE_QUESTIONS_DETECTION = 'DUPLICATE_QUESTIONS_DETECTION',
    GENERAL_AGENT = 'GENERAL_AGENT',
    QNA_CHAT = 'QNA_CHAT',
    GENERAL_AGENT_ASK = 'GENERAL_AGENT_ASK',
    /** AI extraction of questions from the form document. */
    QUESTION_EXTRACTION = 'QUESTION_EXTRACTION',
    /** AI generation of answers for extracted questions. */
    QUESTION_ANSWERING = 'QUESTION_ANSWERING',
}
import { HttpClient } from '@angular/common/http';
import { BaseService } from 'src/app/@core/services/base.service';
import { environment } from 'src/environments/environment.dev';


export interface UserCreditEntry {
    allocated: number;
    consumed: number;
    remaining: number;
    resets: number | null;
    expires: string | null;
}

/**
 * A single token-usage record as returned by the backend.
 * `feature` and `details` are resolved server-side from the TOKEN_FEATURE enum
 * and the associated DB record (document name, thread title, form name, etc.).
 */
export interface UsageRecord {
    /** Human-readable feature label (e.g. "Bob", "Compare", "Question Answering"). */
    feature: string;
    /** Specific document / thread / form name for the run (may be empty for generic features). */
    details: string;
    /** Credit type bucket: "ASK" | "AGENT" | null. */
    creditType: string | null;
    totalTokens: number;
    consumedCredits: number;
    createdAt: string;
    featureId?: string;
}

export interface UserCreditsData {
    ask: UserCreditEntry;
    agent: UserCreditEntry;
    usage: UsageRecord[];
    // credits exhaustion history
    exhaustions: {
        feature: string;
        featureId: string;
        threadId?: string;
        reason: string;
        error?: string;
        agentName: string;
    }[];
}

export interface CreditPricing {
    askCreditRatio: number;
    agentCreditRatio: number;
}

export interface CreditAlertUser {
    user: { _id: string; fullName?: string; firstName?: string; lastName?: string; email?: string };
    type: 'ASK' | 'AGENT';
    allocated: number;
    consumed: number;
    available: number;
    pct: number;
}

export interface OrgAnalyticsData {
    creditSummary: { _id: 'ASK' | 'AGENT'; totalAllocated: number; totalConsumed: number; totalAvailable: number; userCount: number }[];
    seats: { total: number; withCredits: number };
    limitAlerts: { atLimit: CreditAlertUser[]; blocked: CreditAlertUser[]; negativeBalance: CreditAlertUser[] };
}

export interface UsageTrendPoint {
    _id: { year: number; month: number; day: number };
    totalCredits: number;
    totalTokens: number;
    askCredits: number;
    agentCredits: number;
}

@Injectable({ providedIn: 'root' })
export class UserCreditsService extends BaseService {
    constructor(protected override http: HttpClient) {
        super(http);
    }

    private apiUrl = environment.apiUrl + '/user-credits';

    getCurrentCredits() {
        return this.get<UserCreditsData>(`${this.apiUrl}/current`);
    }

    getCreditsByUser(userId: string) {
        return this.post<UserCreditsData>(`${this.apiUrl}/by-user`, { userId });
    }

    getCurrentPricing() {
        return this.get<CreditPricing>(`${this.apiUrl}/current-pricing`);
    }

    checkCreditsAllocationStatus(sessionId: string) {
        return this.post<any>(`${this.apiUrl}/check-credits-allocation-status`, { sessionId });
    }

    getOrgAnalytics(params: { userId?: string; type?: string; model?: string; tier?: string } = {}) {
        const filtered = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== ''));
        return this.get<OrgAnalyticsData>(`${this.apiUrl}/org-analytics`, Object.keys(filtered).length ? filtered : undefined);
    }

    getUsageTrend(params: { startDate?: string; endDate?: string; userId?: string; type?: string } = {}) {
        const filtered = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== ''));
        return this.get<UsageTrendPoint[]>(`${this.apiUrl}/usage-trend`, Object.keys(filtered).length ? filtered : undefined);
    }

    resumeThread(threadId: string, feature: string, featureId: string, agentName: string) {
        return this.post<{ threadId: string }>(`${environment.apiUrl}/thread/resume`, { threadId, feature, featureId, agentName });
    }

    /** Remove a specific credits-exhaustion record for the current user. */
    removeExhaustion(feature: string, featureId: string) {
        return this.post(`${this.apiUrl}/remove-exhaustion`, { feature, featureId });
    }
}
